// ── TTS — voz de salida para Evo Brain (Fase 4) ──────────────────────────────
// Interfaz TtsProvider desacoplada del motor real: v1 usa SpeechSynthesis del
// navegador (gratis, sin dependencia nueva). Cambiar de proveedor después
// (ElevenLabs, etc.) implica una clase nueva que implemente TtsProvider y
// cambiar defaultTtsProvider — nada más en el resto del código.

import { whenAudioUnlocked, isAudioUnlocked, isAutoplayBlocked, installAudioUnlock } from './audioUnlock.js';

/**
 * Reproduce, y si el navegador rechaza por bloqueo de autoplay, espera al
 * primer gesto del usuario y REINTENTA solo — en vez de reportar un error que
 * deja el cartel de "voz desactivada" puesto por algo que no es una falla.
 *
 * Compartido por los dos proveedores de audio (Google TTS y ElevenLabs): el
 * bug era idéntico en los dos y arreglarlo en uno solo lo habría dejado a
 * medias según qué proveedor tuviera configurado la cuenta.
 */
function playWithAutoplayRetry(audio: HTMLAudioElement, callbacks: TtsCallbacks | undefined, onGiveUp: (err: Error) => void): void {
  installAudioUnlock();
  audio.play().catch((err: unknown) => {
    if (!isAutoplayBlocked(err)) {
      onGiveUp(err instanceof Error ? err : new Error(String(err)));
      return;
    }
    // Bloqueo de autoplay: NO es un fallo del audio. Se queda esperando el
    // primer gesto y reintenta; si el audio ya se desbloqueó y aun así falla,
    // ahí sí es un error real.
    whenAudioUnlocked(() => {
      audio.play().catch((err2: unknown) => {
        onGiveUp(err2 instanceof Error ? err2 : new Error(String(err2)));
      });
    });
  });
}

export interface TtsCallbacks {
  onStart?: () => void;
  onEnd?: () => void;
  /** Falla real (fetch, CSP, autoplay rechazado, etc.) — nunca se llama en silencio. */
  onError?: (error: Error) => void;
}

export interface TtsProvider {
  speak(text: string, callbacks?: TtsCallbacks): void;
  stop(): void;
}

export class BrowserSpeechSynthesisProvider implements TtsProvider {
  speak(text: string, callbacks?: TtsCallbacks): void {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
    const trimmed = text.trim();
    if (!trimmed) return;

    window.speechSynthesis.cancel(); // corta cualquier lectura anterior

    const utterance = new SpeechSynthesisUtterance(trimmed);
    if (callbacks?.onStart) utterance.onstart = callbacks.onStart;
    if (callbacks?.onEnd) utterance.onend = callbacks.onEnd;
    utterance.onerror = e => {
      callbacks?.onEnd?.();
      callbacks?.onError?.(new Error(e.error || 'speechSynthesis falló.'));
    };
    window.speechSynthesis.speak(utterance);
  }

  stop(): void {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
  }
}

// v2 — Google Cloud TTS (Neural2, es-US) vía POST /api/assistant/speak. El
// server llama a Google con la key guardada; acá solo se pide el audio y se
// reproduce. Sin key configurada, el server devuelve 400 y esto cae en
// silencio (onEnd sin sonido) — igual que BrowserSpeechSynthesisProvider no
// hace nada si el navegador no soporta speechSynthesis.
export class GoogleCloudTtsProvider implements TtsProvider {
  private audio: HTMLAudioElement | null = null;

  speak(text: string, callbacks?: TtsCallbacks): void {
    const trimmed = text.trim();
    if (!trimmed) return;
    this.stop();

    fetch('/api/assistant/speak', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ text: trimmed }),
    })
      .then(async r => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({})) as { error?: string };
          throw new Error(body.error ?? `HTTP ${r.status}`);
        }
        return r.blob();
      })
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        this.audio = audio;
        audio.onplay = () => callbacks?.onStart?.();
        audio.onended = () => { URL.revokeObjectURL(url); if (this.audio === audio) this.audio = null; callbacks?.onEnd?.(); };
        audio.onerror = () => {
          URL.revokeObjectURL(url);
          if (this.audio === audio) this.audio = null;
          callbacks?.onEnd?.();
          callbacks?.onError?.(new Error('El navegador no pudo cargar el audio (CSP, formato o red).'));
        };
        // audio.play() devuelve una Promise que el navegador puede rechazar
        // (bloqueo de autoplay sin gesto de usuario reciente) — sin .catch()
        // acá ese rechazo queda sin manejar y no llega a ningún lado.
        playWithAutoplayRetry(audio, callbacks, err => {
          if (this.audio === audio) this.audio = null;
          callbacks?.onEnd?.();
          callbacks?.onError?.(err);
        });
      })
      .catch((err: unknown) => {
        const error = err instanceof Error ? err : new Error(String(err));
        console.error('[GoogleCloudTtsProvider] speak failed:', error);
        callbacks?.onEnd?.();
        callbacks?.onError?.(error);
      });
  }

  stop(): void {
    if (this.audio) {
      this.audio.pause();
      this.audio.currentTime = 0;
      this.audio = null;
    }
  }
}

// v3 — ElevenLabs (Flash v2.5), elegida por Eduardo: la única que se prueba
// sin tarjeta, la que mejor suena en español, ~$9/mes a este volumen. Mismo
// patrón que GoogleCloudTtsProvider — pide el audio a un endpoint server y lo
// reproduce con Audio(). El voice_id se elige server-side (setting DB
// "elevenlabs-voice-id", ver assistant.ts) — acá no hace falta tocar nada
// para cambiar de voz.
export class ElevenLabsTtsProvider implements TtsProvider {
  private audio: HTMLAudioElement | null = null;

  speak(text: string, callbacks?: TtsCallbacks): void {
    const trimmed = text.trim();
    if (!trimmed) return;
    this.stop();

    fetch('/api/assistant/speak-elevenlabs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ text: trimmed }),
    })
      .then(async r => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({})) as { error?: string };
          throw new Error(body.error ?? `HTTP ${r.status}`);
        }
        return r.blob();
      })
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        this.audio = audio;
        audio.onplay = () => callbacks?.onStart?.();
        audio.onended = () => { URL.revokeObjectURL(url); if (this.audio === audio) this.audio = null; callbacks?.onEnd?.(); };
        audio.onerror = () => {
          URL.revokeObjectURL(url);
          if (this.audio === audio) this.audio = null;
          callbacks?.onEnd?.();
          callbacks?.onError?.(new Error('El navegador no pudo cargar el audio (CSP, formato o red).'));
        };
        // audio.play() devuelve una Promise que el navegador puede rechazar
        // (bloqueo de autoplay sin gesto de usuario reciente) — sin .catch()
        // acá ese rechazo queda sin manejar y no llega a ningún lado.
        playWithAutoplayRetry(audio, callbacks, err => {
          if (this.audio === audio) this.audio = null;
          callbacks?.onEnd?.();
          callbacks?.onError?.(err);
        });
      })
      .catch((err: unknown) => {
        const error = err instanceof Error ? err : new Error(String(err));
        console.error('[ElevenLabsTtsProvider] speak failed:', error);
        callbacks?.onEnd?.();
        callbacks?.onError?.(error);
      });
  }

  stop(): void {
    if (this.audio) {
      this.audio.pause();
      this.audio.currentTime = 0;
      this.audio = null;
    }
  }
}

// GoogleCloudTtsProvider queda implementada y disponible (server + secret ya
// wireados) pero SIN activar. Activo hoy: ElevenLabsTtsProvider.
export const defaultTtsProvider: TtsProvider = new ElevenLabsTtsProvider();

// ── Extracción de texto hablable ─────────────────────────────────────────────
// Links, tablas y bloques de código no se leen — se sacan del texto y se
// reemplazan por un aviso corto (provisto por el caller, ya traducido).

export interface SpeakableNotes {
  linkNote: string;
  tableNote: string;
}

export function extractSpeakableText(markdown: string, notes: SpeakableNotes): string {
  let text = markdown;
  let hasTable = false;
  let hasLink = false;

  // Bloques de código — no se leen
  text = text.replace(/```[\s\S]*?```/g, ' ');
  text = text.replace(/`([^`]+)`/g, '$1');

  // Tablas GFM: fila de encabezado + fila separadora (|---|---|) + filas de datos
  text = text.replace(/^(\|.+\|\n)(\|[\s:-]+\|\n)(\|.+\|\n?)+/gm, () => {
    hasTable = true;
    return ' ';
  });

  // Links markdown [label](url) y URLs sueltas
  text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, () => {
    hasLink = true;
    return ' ';
  });
  text = text.replace(/https?:\/\/\S+/g, () => {
    hasLink = true;
    return ' ';
  });

  // Resto de sintaxis markdown — se lee como prosa plana
  text = text
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/^[-*]\s+/gm, '')
    .replace(/\n{2,}/g, '. ')
    .replace(/\n/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();

  const suffix = [hasTable ? notes.tableNote : null, hasLink ? notes.linkNote : null].filter(Boolean);
  return [text, ...suffix].filter(Boolean).join(' ');
}
